import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Plus, Trash2, Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { t } from "@/lib/i18n";

const FINDING_TYPES = ["produttore", "fornitore", "competitor", "prodotto", "flusso", "evento", "stakeholder", "normativa"];

const List = ({ title, items, color }) => items.length > 0 && (
  <div><div className="eyebrow mb-1.5">{title} · {items.length}</div><div className="flex flex-wrap gap-1.5">{items.map((i) => <span key={i} className="text-xs px-2 py-1 rounded-full border" style={{ borderColor: color, color }}>{i}</span>)}</div></div>
);

export default function TerritoryPage() {
  const [regions, setRegions] = useState([]);
  const [region, setRegion] = useState(null);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [dossiers, setDossiers] = useState([]);
  const [checklist, setChecklist] = useState([]);
  const [nd, setNd] = useState({ name: "", area: "", objective: "" });
  const [nf, setNf] = useState({ type: "produttore", name: "", note: "", rating: 3 });
  const [activeDossier, setActiveDossier] = useState(null);

  const loadDossiers = () => api.get("/territory/dossiers").then((r) => setDossiers(r.data));
  useEffect(() => { api.get("/territory/regions").then((r) => setRegions(r.data)); api.get("/territory/checklist").then((r) => setChecklist(r.data)); loadDossiers(); }, []);
  useEffect(() => { if (q.length < 2) return setHits([]); const t = setTimeout(() => api.get("/territory/search", { params: { q } }).then((r) => setHits(r.data)), 250); return () => clearTimeout(t); }, [q]);
  const openRegion = (id) => api.get(`/territory/regions/${id}`).then((r) => { setRegion(r.data); setHits([]); setQ(""); });

  const createDossier = async () => {
    if (!region || !nd.name) return toast.error("Seleziona una regione e dai un nome al dossier");
    await api.post("/territory/dossiers", { ...nd, region_id: region.id }); setNd({ name: "", area: "", objective: "" }); toast.success("Dossier creato"); loadDossiers();
  };
  const addFinding = async (id) => {
    if (!nf.name) return toast.error("Inserisci un nome");
    await api.post(`/territory/dossiers/${id}/findings`, { ...nf, rating: +nf.rating }); setNf({ type: "produttore", name: "", note: "", rating: 3 }); loadDossiers();
  };
  const delDossier = (id) => api.delete(`/territory/dossiers/${id}`).then(loadDossiers);

  return (
    <div data-testid="territory-page">
      <PageHeader eyebrow={t("Territory / Product Intelligence")} title={t("Ricerca territoriale")}
        subtitle={t("Denominazioni, vitigni, prodotti e tratti di ogni regione italiana; dossier di ricerca con schede produttori, competitor, flussi e stakeholder, guidati dalla checklist metodologica del corpus.")}>
        <div className="relative w-full lg:w-96">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="territory-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Cerca denominazione, vitigno, prodotto…")} className="pl-9 bg-card" />
          {hits.length > 0 && (
            <div data-testid="territory-search-results" className="absolute z-40 mt-2 w-full surface-elevated shadow-2xl max-h-80 overflow-auto p-2">
              {hits.map((h, i) => <button key={i} data-testid="territory-search-hit" onClick={() => openRegion(h.region_id)} className="w-full text-left px-3 py-2 rounded-md hover:bg-accent text-sm flex justify-between gap-2"><span className="truncate">{h.item}</span><span className="text-xs text-muted-foreground shrink-0">{h.category} · {h.region}</span></button>)}
            </div>
          )}
        </div>
      </PageHeader>

      <div className="px-5 lg:px-8 py-6 grid xl:grid-cols-[280px_1fr_340px] gap-6">
        <aside className="space-y-1.5">
          <div className="eyebrow mb-2">{t("Regioni")}</div>
          {regions.map((r) => (
            <button key={r.id} data-testid={`region-btn-${r.id}`} onClick={() => openRegion(r.id)} className={`w-full text-left px-3 py-2 rounded-md border text-sm flex justify-between transition-colors ${region?.id === r.id ? "border-primary bg-primary/10" : "border-border hover:bg-accent"}`}>
              <span>{r.name}</span><span className="num text-[10px] text-muted-foreground">{r.counts.wine}v · {r.counts.oil}o · {r.counts.food}f</span>
            </button>
          ))}
        </aside>

        <section className="space-y-5 min-w-0">
          {region ? (
            <div data-testid="region-detail" className="surface p-5 space-y-4 fade-up">
              <div><div className="eyebrow">{region.macro} · capoluogo {region.capital}</div><h2 className="text-2xl font-bold">{region.name}</h2></div>
              <p className="text-sm text-foreground/85"><span className="text-primary font-medium">{t("Tratti gastronomici ·")} </span>{region.traits}</p>
              <p className="text-sm text-foreground/85"><span className="text-primary font-medium">{t("Turismo e flussi ·")} </span>{region.tourism}</p>
              <List title={t("Vino")} items={region.wine} color="#9B59B6" />
              <List title={t("Vitigni")} items={region.grapes} color="#E67E22" />
              <List title={t("Olio")} items={region.oil} color="#2ECC71" />
              <List title={t("Food DOP / IGP")} items={region.food} color="#F1C40F" />
              <div className="surface-elevated p-4 space-y-2">
                <div className="eyebrow">Nuovo dossier su {region.name}</div>
                <Input data-testid="dossier-name-input" placeholder={t("Nome del dossier")} value={nd.name} onChange={(e) => setNd({ ...nd, name: e.target.value })} className="bg-background" />
                <Input data-testid="dossier-area-input" placeholder={t("Area specifica (comune, valle, costa…)")} value={nd.area} onChange={(e) => setNd({ ...nd, area: e.target.value })} className="bg-background" />
                <Textarea data-testid="dossier-objective-input" placeholder={t("Obiettivo della ricerca")} value={nd.objective} onChange={(e) => setNd({ ...nd, objective: e.target.value })} className="bg-background min-h-[60px]" />
                <Button data-testid="dossier-create-btn" size="sm" onClick={createDossier}><Plus size={14} className="mr-1" /> {t("Crea dossier")}</Button>
              </div>
            </div>
          ) : <div className="surface p-8 text-center text-sm text-muted-foreground">{t("Seleziona una regione o cerca una denominazione.")}</div>}

          <div className="surface p-5">
            <div className="eyebrow mb-3">{t("Checklist metodologica di ricerca")}</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {checklist.map((c) => (
                <div key={c.area} className="surface-elevated p-3">
                  <div className="text-sm font-medium mb-1">{c.area}</div>
                  <ul className="text-xs text-muted-foreground space-y-0.5">{c.items.map((i) => <li key={i}>• {i}</li>)}</ul>
                  <div className="flex flex-wrap gap-1 mt-2">{c.nodes.map((n) => <Link key={n} to={`/?node=${n}`} className="text-[10px] text-primary/80 hover:text-primary">↗ {n}</Link>)}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <aside className="space-y-3">
          <div className="eyebrow">Dossier ({dossiers.length})</div>
          {dossiers.map((d) => (
            <div key={d.id} data-testid={`dossier-card-${d.id}`} className="surface p-4 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <button onClick={() => setActiveDossier(activeDossier === d.id ? null : d.id)} className="text-left flex-1"><div className="font-medium text-sm">{d.name}</div><div className="text-xs text-muted-foreground">{regions.find((r) => r.id === d.region_id)?.name}{d.area ? ` · ${d.area}` : ""} · {d.findings.length} schede</div></button>
                <button data-testid={`dossier-delete-${d.id}`} onClick={() => delDossier(d.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={14} /></button>
              </div>
              {activeDossier === d.id && (
                <div className="space-y-2 pt-2 border-t border-border">
                  {d.objective && <p className="text-xs text-foreground/80">{d.objective}</p>}
                  {d.findings.map((f) => (
                    <div key={f.id} className="surface-elevated p-2.5 text-xs">
                      <div className="flex items-center justify-between"><span className="uppercase tracking-wider text-[10px] text-primary">{f.type}</span><span className="flex gap-0.5">{Array.from({ length: f.rating }).map((_, i) => <Star key={i} size={10} className="fill-amber-400 text-amber-400" />)}</span></div>
                      <div className="font-medium mt-0.5">{f.name}</div>{f.note && <div className="text-muted-foreground mt-0.5">{f.note}</div>}
                    </div>
                  ))}
                  <div className="space-y-1.5 pt-1">
                    <div className="flex gap-1.5">
                      <select data-testid="finding-type-select" value={nf.type} onChange={(e) => setNf({ ...nf, type: e.target.value })} className="h-9 rounded-md border border-border bg-background px-2 text-xs flex-1">{FINDING_TYPES.map((t) => <option key={t}>{t}</option>)}</select>
                      <select data-testid="finding-rating-select" value={nf.rating} onChange={(e) => setNf({ ...nf, rating: e.target.value })} className="h-9 rounded-md border border-border bg-background px-2 text-xs w-16">{[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}★</option>)}</select>
                    </div>
                    <Input data-testid="finding-name-input" placeholder={t("Nome")} value={nf.name} onChange={(e) => setNf({ ...nf, name: e.target.value })} className="bg-background h-9 text-xs" />
                    <Input data-testid="finding-note-input" placeholder={t("Nota")} value={nf.note} onChange={(e) => setNf({ ...nf, note: e.target.value })} className="bg-background h-9 text-xs" />
                    <Button data-testid="finding-add-btn" size="sm" variant="outline" className="w-full" onClick={() => addFinding(d.id)}><Plus size={13} className="mr-1" /> {t("Aggiungi scheda")}</Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </aside>
      </div>
    </div>
  );
}
